import {
  CompanyCandidateOptionListQueryVariables,
  OrderType,
  PlatformCompanyCandidateListOrderFields,
  useCompanyCandidateOptionListQuery,
} from "@generated/graphql";
import { AutocompleteInputChangeReason, Button, Stack } from "@mui/material";
import FieldLabel from "components/FieldLabel";
import Autocomplete from "components/inputs/Autocomplete";
import Select from "components/inputs/Select";
import { AUTH_SETTING_OPTIONS, AuthSettingName, CandidateSearchType } from "constants/candidate.constants";
import { GRADE_OPTIONS } from "constants/global";
import ROUTES from "constants/routes";
import { CompanyContext } from "context/companyContext";
import { getTelegramUserName } from "helpers/form.helpers";
import useIsCreate from "hooks/useIsCreate";
import { debounce, isEqual } from "lodash";
import { useCallback, useContext, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Link } from "react-router-dom";
import { palette } from "theme";
import { ICandidateItem, InfoInputs } from "../types";

interface CandidateSearchProps {
  isEditing: boolean;
}

const CandidateSearch = ({ isEditing }: CandidateSearchProps) => {
  const { setValue, watch } = useFormContext<InfoInputs>();
  const [candidateSearchType, setCandidateSearchType] = useState(CandidateSearchType.FULL_NAME);
  const [candidateSearchValue, setCandidateSearchValue] = useState<ICandidateItem | string | null>(null);
  const { currentCompanyId } = useContext(CompanyContext);
  const isCreate = useIsCreate();

  const candidate = watch("candidate");

  const defaultCompanyCandidateOptionListQueryVariables: CompanyCandidateOptionListQueryVariables = {
    page: 0,
    limit: 15,
    order: { order: OrderType.Desc, field: PlatformCompanyCandidateListOrderFields.CreatedAt },
  };

  const {
    data,
    loading,
    variables,
    refetch: refetchCompanyCandidateOptionList,
  } = useCompanyCandidateOptionListQuery({
    variables: {
      ...defaultCompanyCandidateOptionListQueryVariables,
    },
    notifyOnNetworkStatusChange: true,
  });

  const candidateList: ICandidateItem[] = useMemo(
    () =>
      data?.companyCandidates.data?.map((candidateItem) => {
        const { candidateUsername, id, fullName, numericId } = candidateItem;
        const companyCandidate = candidateUsername?.companyCandidate?.find((c) => c.companyId === currentCompanyId);

        return {
          id,
          numericId: numericId.toString() || "",
          fullName,
          telegramUsername: candidateUsername?.telegramUsername || "",
          phoneNumber: candidateUsername?.candidate?.phoneNumber?.replace("+", "") || "",
          email: candidateUsername?.email || "",
          workEmail: companyCandidate?.email || "",
          position: companyCandidate?.position || "",
          login: candidateUsername?.login || "",
          grade: GRADE_OPTIONS?.find((grade) => companyCandidate?.grade === grade.id) || null,
          organization: companyCandidate?.organization || "",
          yandexEmail: candidateUsername?.yandexEmail || "",
          vkId: candidateUsername?.vkId || "",
        };
      }) || [],
    [data]
  );

  const getSearchValue = (inputValue: string | undefined) => {
    if (inputValue === undefined) return inputValue;

    if (candidateSearchType === CandidateSearchType.NUMERIC_ID) {
      return Number(inputValue);
    }
    if (candidateSearchType === CandidateSearchType.TELEGRAM) {
      return inputValue.replace("@", "");
    }

    return inputValue;
  };

  const debounceQuery = useCallback(
    debounce((inputValue: string | undefined) => {
      const value = getSearchValue(inputValue);
      const refetchVariables: CompanyCandidateOptionListQueryVariables = {
        filter: value ? { [candidateSearchType]: value } : undefined,
        ...defaultCompanyCandidateOptionListQueryVariables,
      };

      if (!isEqual(variables, refetchVariables)) {
        refetchCompanyCandidateOptionList(refetchVariables);
      }
    }, 500),
    [variables, candidateSearchType]
  );

  const onAutocompleteInputChange = (inputValue: string, reason: AutocompleteInputChangeReason) => {
    if (reason === "input") {
      return debounceQuery(inputValue);
    }

    return debounceQuery(undefined);
  };

  const setCandidateData = (candidateData: ICandidateItem) => {
    [
      { name: "candidate.fullName", value: candidateData?.fullName },
      { name: "candidate.email", value: candidateData?.email },
      { name: "candidate.numericId", value: candidateData?.numericId },
      { name: "candidate.grade", value: GRADE_OPTIONS?.find((grade) => candidateData?.grade?.id === grade.id) },
      { name: "candidate.organization", value: candidateData?.organization },
      { name: "candidate.position", value: candidateData?.position },
      { name: "candidate.workEmail", value: candidateData?.workEmail },
      { name: "candidate.phoneNumber", value: candidateData?.phoneNumber },
      { name: "candidate.id", value: candidateData?.id },
      { name: "candidate.telegramUsername", value: getTelegramUserName(candidateData) },
      { name: "candidate.login", value: candidateData?.login },
      { name: "candidate.yandexEmail", value: candidateData?.yandexEmail },
      { name: "candidate.vkId", value: candidateData?.vkId },
      { name: `authSettingsSwitch.${AuthSettingName.EMAIL}`, value: !!candidateData?.email },
      { name: `authSettingsSwitch.${AuthSettingName.YANDEX_ID}`, value: !!candidateData?.yandexEmail },
      { name: `authSettingsSwitch.${AuthSettingName.LOGIN}`, value: !!candidateData?.login },
      { name: `authSettingsSwitch.${AuthSettingName.VK_ID}`, value: !!candidateData?.vkId },
      { name: `authSettingsSwitch.${AuthSettingName.TELEGRAM}`, value: !!candidateData?.telegramUsername },
    ].forEach(({ name, value }) => setValue(name as keyof InfoInputs, value as InfoInputs[keyof InfoInputs]));
  };

  const handleAutocompleteChange = (candidateData: ICandidateItem | null | string): ICandidateItem | string | null => {
    if (!candidateData || typeof candidateData === "string" || !Object.hasOwn(candidateData, "id")) {
      if (candidateSearchType === CandidateSearchType.NUMERIC_ID) return candidate.numericId || null;
      return candidateData;
    }

    setCandidateData(candidateData);
    return candidateData;
  };

  const handleCandidateSearchTypeChange = (value: CandidateSearchType) => {
    setCandidateSearchValue(null);
    setCandidateSearchType(value);
  };

  return (
    <Stack spacing={0.5}>
      <FieldLabel
        label='Поиск кандидата'
        required={isEditing}
      />
      <Stack
        direction='row'
        spacing={1}>
        {isCreate && (
          <>
            <Select
              formControlProps={{ sx: { width: 150 } }}
              sx={{ backgroundColor: palette.bgSwitch }}
              items={AUTH_SETTING_OPTIONS}
              value={candidateSearchType}
              onChange={(e) => handleCandidateSearchTypeChange(e.target.value as CandidateSearchType)}
            />
            <Autocomplete<ICandidateItem, false, false, true, ICandidateItem>
              onChange={(value) => setCandidateSearchValue(value as ICandidateItem | string | null)}
              value={candidateSearchValue}
              placeholder='Значение'
              optionName='fullName'
              searchParam={candidateSearchType}
              options={candidateList}
              loading={loading}
              onInputChange={(event, value, reason) => onAutocompleteInputChange(value, reason)}
              formatDataBeforeOnChange={handleAutocompleteChange}
              stackSx={{ width: "100%" }}
              noOptionsText={
                <Button
                  color='secondary'
                  size='medium'
                  disableRipple
                  sx={{
                    paddingX: 1,
                    wordBreak: "normal",
                    pb: 0,
                  }}
                  component={Link}
                  to={`/${ROUTES.CANDIDATE}/create`}
                  target='_blank'>
                  Создать кандидата
                </Button>
              }
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};

export default CandidateSearch;
